/**
 * Colour language (shared by GL shaders, the 2D label layer and DOM CSS):
 *   green = you, teal = allies, amber/orange family = enemies, red = imminent danger only,
 *   dim grey-green = neutral / unknown.
 * Colours are linear 0..1 RGB triples so they can go straight into uniforms.
 */
export type RGB = readonly [number, number, number];

const c = (r: number, g: number, b: number): RGB => [r / 255, g / 255, b / 255];

export const COLORS = {
  bg: c(3, 8, 6),
  me: c(96, 255, 160),
  ally: c(84, 214, 226),
  danger: c(255, 62, 52),
  dangerSoft: c(255, 112, 98),
  white: c(214, 255, 228),
  neutral: c(150, 170, 160),
  amber: c(255, 172, 52),
  /** a front we are losing: between amber and the danger reds */
  losing: c(255, 136, 62),
} as const;

/**
 * Enemy palette: up to 7 enemies stay distinguishable through brightness steps within the
 * amber → gold → sand family (hue 35°–55°, well clear of the danger reds) *and* a distinct
 * fill pattern (see territory shader `pattern()`). Patterns, for UI swatches:
 *   0 horizontal lines · 1 vertical lines · 2 cross-hatch · 3 anti-diagonal hatch ·
 *   4 coarse dots · 5 dashes · 6 grid; you 7 diagonal hatch; allies 8 staggered dots.
 */
export const ENEMY_STYLES: readonly { color: RGB; pattern: number }[] = [
  { color: c(255, 176, 56), pattern: 0 }, // amber
  { color: c(242, 214, 96), pattern: 1 }, // gold
  { color: c(208, 146, 52), pattern: 2 }, // deep ochre
  { color: c(218, 184, 118), pattern: 3 }, // muted sand (a pale one read as white under bloom)
  { color: c(196, 170, 80), pattern: 4 }, // olive gold
  { color: c(255, 196, 92), pattern: 5 }, // light amber
  { color: c(176, 132, 70), pattern: 6 }, // bronze
];

export const PATTERN_ME = 7;
export const PATTERN_ALLY = 8;
export const PATTERN_NEUTRAL = 9;

export function cssRgb(col: RGB, alpha = 1): string {
  const r = Math.round(col[0] * 255);
  const g = Math.round(col[1] * 255);
  const b = Math.round(col[2] * 255);
  return alpha >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${alpha})`;
}

export const FONT_STACK = `'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace`;
